/**
 * Mock product data for PetJr Shop
 * Categories: Apparel, Drinkware, Home, Accessories, Prints
 * 
 * Now includes variant-specific images for color changes
 */

export const PRODUCT_CATEGORIES = [
  { id: 'all', name: 'All Products', emoji: '🛍️' },
  { id: 'apparel', name: 'Clothing', emoji: '👕' },
  { id: 'drinkware', name: 'Drinkware', emoji: '☕' },
  { id: 'home', name: 'Home & Living', emoji: '🏠' },
  { id: 'accessories', name: 'Accessories', emoji: '🎒' },
  { id: 'prints', name: 'Prints & Posters', emoji: '🖼️' },
];

// Generate realistic review data
const generateReviews = (count, avgRating) => {
  const reviews = [];
  const reviewTexts = [
    { text: "Absolutely love it! My dog looks so cute!", name: "Sarah M.", verified: true },
    { text: "Great quality, arrived quickly. Will order again!", name: "Mike R.", verified: true },
    { text: "Perfect gift for my mom who loves her cat.", name: "Emily K.", verified: true },
    { text: "The colors are vibrant and the material is soft.", name: "David L.", verified: true },
    { text: "Exceeded expectations! Everyone asks where I got it.", name: "Lisa T.", verified: true },
    { text: "Good product but shipping took a bit longer than expected.", name: "James H.", verified: false },
    { text: "My friends are so jealous! Ordering more for gifts.", name: "Amanda C.", verified: true },
    { text: "The portrait capture is amazing. Looks just like my pet!", name: "Chris P.", verified: true },
    { text: "High quality print that doesn't fade after washing.", name: "Rachel S.", verified: true },
    { text: "Super fast delivery and great customer service!", name: "Tom B.", verified: true },
  ];
  
  for (let i = 0; i < count; i++) {
    const review = reviewTexts[i % reviewTexts.length];
    // Generate rating close to average with some variation
    let rating = avgRating + (Math.random() * 1.5 - 0.75);
    rating = Math.min(5, Math.max(3, Math.round(rating * 2) / 2));
    reviews.push({ ...review, rating });
  }
  return reviews;
};

export const PRODUCTS = [
  // Apparel
  {
    id: 'tshirt-unisex',
    name: 'Unisex T-Shirt',
    category: 'apparel',
    price: 24.99,
    description: 'Soft, comfortable cotton t-shirt featuring your pet\'s portrait.',
    variants: [
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=400' },
      { id: 'white', name: 'White', color: '#ffffff', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400' },
      { id: 'navy', name: 'Navy', color: '#1e3a5f', image: 'https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=400' },
      { id: 'heather-gray', name: 'Heather Gray', color: '#9ca3af', image: 'https://images.unsplash.com/photo-1581655353564-df123a1eb820?w=400' },
    ],
    sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    mockupImage: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400',
    popular: true,
    rating: 4.8,
    reviewCount: 342,
    reviews: generateReviews(10, 4.8),
  },
  {
    id: 'hoodie-pullover',
    name: 'Pullover Hoodie',
    category: 'apparel',
    price: 44.99,
    description: 'Cozy pullover hoodie with your pet proudly displayed.',
    variants: [
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400' },
      { id: 'navy', name: 'Navy', color: '#1e3a5f', image: 'https://images.unsplash.com/photo-1620799140188-3b2a02fd9a77?w=400' },
      { id: 'forest', name: 'Forest Green', color: '#228b22', image: 'https://images.unsplash.com/photo-1578768079052-aa76e52ff62e?w=400' },
    ],
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    mockupImage: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400',
    popular: true,
    rating: 4.9,
    reviewCount: 187,
  },
  {
    id: 'tank-top',
    name: 'Tank Top',
    category: 'apparel',
    price: 22.99,
    description: 'Lightweight tank top perfect for showing off your pet.',
    variants: [
      { id: 'white', name: 'White', color: '#ffffff', image: 'https://images.unsplash.com/photo-1503341504253-dff4815485f1?w=400' },
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?w=400' },
    ],
    sizes: ['S', 'M', 'L', 'XL'],
    mockupImage: 'https://images.unsplash.com/photo-1503341504253-dff4815485f1?w=400',
    popular: false,
    rating: 4.5,
    reviewCount: 89,
  },
  {
    id: 'sweatshirt-crew',
    name: 'Crew Neck Sweatshirt',
    category: 'apparel',
    price: 39.99,
    description: 'Classic crew neck sweatshirt with your pet\'s artwork.',
    variants: [
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1578587018452-892bacefd3f2?w=400' },
      { id: 'heather-gray', name: 'Heather Gray', color: '#9ca3af', image: 'https://images.unsplash.com/photo-1572495641004-28421ae29ed4?w=400' },
    ],
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    mockupImage: 'https://images.unsplash.com/photo-1578587018452-892bacefd3f2?w=400',
    popular: false,
    rating: 4.6,
    reviewCount: 124,
  },
  
  // Drinkware
  {
    id: 'mug-11oz',
    name: 'Classic Mug (11oz)',
    category: 'drinkware',
    price: 14.99,
    description: 'Start your day with coffee and your favorite pet!',
    variants: [
      { id: 'white', name: 'White', color: '#ffffff', image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?w=400' },
    ],
    sizes: ['11oz'],
    mockupImage: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?w=400',
    popular: true,
    rating: 4.7,
    reviewCount: 512,
  },
  {
    id: 'mug-15oz',
    name: 'Large Mug (15oz)',
    category: 'drinkware',
    price: 17.99,
    description: 'Extra-large mug for extra pet love!',
    variants: [
      { id: 'white', name: 'White', color: '#ffffff', image: 'https://images.unsplash.com/photo-1577937927133-66ef06acdf18?w=400' },
    ],
    sizes: ['15oz'],
    mockupImage: 'https://images.unsplash.com/photo-1577937927133-66ef06acdf18?w=400',
    popular: false,
    rating: 4.6,
    reviewCount: 78,
  },
  {
    id: 'tumbler-20oz',
    name: 'Insulated Tumbler (20oz)',
    category: 'drinkware',
    price: 29.99,
    description: 'Keep your drinks hot or cold with your pet by your side.',
    variants: [
      { id: 'stainless', name: 'Stainless Steel', color: '#c0c0c0', image: 'https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=400' },
      { id: 'black', name: 'Matte Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1570784879529-61eab8dcbbaf?w=400' },
    ],
    sizes: ['20oz'],
    mockupImage: 'https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=400',
    popular: true,
    rating: 4.8,
    reviewCount: 234,
  },
  {
    id: 'water-bottle',
    name: 'Water Bottle',
    category: 'drinkware',
    price: 24.99,
    description: 'Stainless steel water bottle with pet portrait.',
    variants: [
      { id: 'white', name: 'White', color: '#ffffff', image: 'https://images.unsplash.com/photo-1523362628745-0c100150b504?w=400' },
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1600166898405-da9535204843?w=400' },
    ],
    sizes: ['17oz'],
    mockupImage: 'https://images.unsplash.com/photo-1523362628745-0c100150b504?w=400',
    popular: false,
    rating: 4.5,
    reviewCount: 67,
  },
  
  // Home & Living
  {
    id: 'throw-pillow',
    name: 'Throw Pillow',
    category: 'home',
    price: 29.99,
    description: 'Cozy throw pillow featuring your pet\'s portrait.',
    variants: [
      { id: 'white', name: 'White', color: '#ffffff', image: 'https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?w=400' },
    ],
    sizes: ['16x16"', '18x18"', '20x20"'],
    mockupImage: 'https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?w=400',
    popular: true,
    rating: 4.9,
    reviewCount: 156,
  },
  {
    id: 'fleece-blanket',
    name: 'Fleece Blanket',
    category: 'home',
    price: 49.99,
    description: 'Super soft fleece blanket with your pet\'s image.',
    variants: [
      { id: 'white', name: 'White Background', color: '#ffffff', image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400' },
    ],
    sizes: ['50x60"', '60x80"'],
    mockupImage: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400',
    popular: true,
    rating: 4.8,
    reviewCount: 203,
  },
  {
    id: 'wall-clock',
    name: 'Wall Clock',
    category: 'home',
    price: 34.99,
    description: 'Keep time with your pet watching over you.',
    variants: [
      { id: 'white', name: 'White Frame', color: '#ffffff', image: 'https://images.unsplash.com/photo-1563861826100-9cb868fdbe1c?w=400' },
      { id: 'black', name: 'Black Frame', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1508057198894-247b23fe5ade?w=400' },
    ],
    sizes: ['10"'],
    mockupImage: 'https://images.unsplash.com/photo-1563861826100-9cb868fdbe1c?w=400',
    popular: false,
    rating: 4.4,
    reviewCount: 45,
  },
  {
    id: 'doormat',
    name: 'Doormat',
    category: 'home',
    price: 39.99,
    description: 'Welcome guests with your pet at the door!',
    variants: [
      { id: 'natural', name: 'Natural', color: '#d4a574', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400' },
    ],
    sizes: ['18x24"', '24x36"'],
    mockupImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
    popular: false,
    rating: 4.6,
    reviewCount: 92,
  },
  
  // Accessories
  {
    id: 'tote-bag',
    name: 'Canvas Tote Bag',
    category: 'accessories',
    price: 19.99,
    description: 'Eco-friendly tote bag with your pet\'s portrait.',
    variants: [
      { id: 'natural', name: 'Natural', color: '#f5f5dc', image: 'https://images.unsplash.com/photo-1544816155-12df9643f363?w=400' },
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1591561954557-26941169b49e?w=400' },
    ],
    sizes: ['15x15"'],
    mockupImage: 'https://images.unsplash.com/photo-1544816155-12df9643f363?w=400',
    popular: true,
    rating: 4.7,
    reviewCount: 278,
  },
  {
    id: 'phone-case',
    name: 'Phone Case',
    category: 'accessories',
    price: 24.99,
    description: 'Protect your phone with your pet\'s face.',
    variants: [
      { id: 'clear', name: 'Clear', color: 'transparent', image: 'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?w=400' },
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=400' },
    ],
    sizes: ['iPhone 14', 'iPhone 14 Pro', 'iPhone 15', 'iPhone 15 Pro', 'Samsung S23', 'Samsung S24'],
    mockupImage: 'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?w=400',
    popular: true,
    rating: 4.6,
    reviewCount: 445,
  },
  {
    id: 'phone-case-folio',
    name: 'Phone Wallet Case',
    category: 'accessories',
    price: 32.99,
    description: 'Folio-style case with card slots and your pet on the cover.',
    variants: [
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1603314585447-3cf5e8e62339?w=400' },
      { id: 'brown', name: 'Brown', color: '#5c4033', image: 'https://images.unsplash.com/photo-1556656793-08538906a9f8?w=400' },
    ],
    sizes: ['iPhone 14', 'iPhone 15', 'iPhone 15 Pro', 'Samsung S24'],
    mockupImage: 'https://images.unsplash.com/photo-1603314585447-3cf5e8e62339?w=400',
    popular: false,
    rating: 4.5,
    reviewCount: 128,
  },
  {
    id: 'backpack',
    name: 'Backpack',
    category: 'accessories',
    price: 54.99,
    description: 'Stylish backpack featuring your pet.',
    variants: [
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400' },
    ],
    sizes: ['One Size'],
    mockupImage: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400',
    popular: false,
    rating: 4.5,
    reviewCount: 34,
  },
  {
    id: 'hat-dad',
    name: 'Dad Hat',
    category: 'accessories',
    price: 26.99,
    description: 'Classic dad hat with embroidered pet silhouette.',
    variants: [
      { id: 'black', name: 'Black', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=400' },
      { id: 'navy', name: 'Navy', color: '#1e3a5f', image: 'https://images.unsplash.com/photo-1534215754734-18e55d13e346?w=400' },
      { id: 'khaki', name: 'Khaki', color: '#c3b091', image: 'https://images.unsplash.com/photo-1521369909029-2afed882baee?w=400' },
    ],
    sizes: ['One Size'],
    mockupImage: 'https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=400',
    popular: false,
    rating: 4.4,
    reviewCount: 56,
  },
  
  // Prints & Posters
  {
    id: 'poster-12x18',
    name: 'Art Print (12x18")',
    category: 'prints',
    price: 19.99,
    description: 'High-quality art print of your pet\'s portrait.',
    variants: [
      { id: 'matte', name: 'Matte', color: '#ffffff', image: 'https://images.unsplash.com/photo-1513519245088-0e12902e35a6?w=400' },
      { id: 'glossy', name: 'Glossy', color: '#ffffff', image: 'https://images.unsplash.com/photo-1513519245088-0e12902e35a6?w=400' },
    ],
    sizes: ['12x18"'],
    mockupImage: 'https://images.unsplash.com/photo-1513519245088-0e12902e35a6?w=400',
    popular: true,
    rating: 4.9,
    reviewCount: 389,
  },
  {
    id: 'canvas-16x20',
    name: 'Canvas Print (16x20")',
    category: 'prints',
    price: 59.99,
    description: 'Gallery-quality canvas print ready to hang.',
    variants: [
      { id: 'wrapped', name: 'Gallery Wrapped', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400' },
    ],
    sizes: ['16x20"'],
    mockupImage: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400',
    popular: true,
    rating: 4.8,
    reviewCount: 267,
  },
  {
    id: 'canvas-24x36',
    name: 'Large Canvas (24x36")',
    category: 'prints',
    price: 99.99,
    description: 'Statement piece canvas for your home.',
    variants: [
      { id: 'wrapped', name: 'Gallery Wrapped', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1561214115-f2f134cc4912?w=400' },
    ],
    sizes: ['24x36"'],
    mockupImage: 'https://images.unsplash.com/photo-1561214115-f2f134cc4912?w=400',
    popular: false,
    rating: 4.7,
    reviewCount: 89,
  },
  {
    id: 'framed-print',
    name: 'Framed Art Print',
    category: 'prints',
    price: 79.99,
    description: 'Professionally framed print of your pet.',
    variants: [
      { id: 'black', name: 'Black Frame', color: '#1a1a1a', image: 'https://images.unsplash.com/photo-1513519245088-0e12902e35a6?w=400' },
      { id: 'white', name: 'White Frame', color: '#ffffff', image: 'https://images.unsplash.com/photo-1582201942988-13e60e4556ee?w=400' },
      { id: 'natural', name: 'Natural Wood', color: '#8b7355', image: 'https://images.unsplash.com/photo-1544967082-d9d25d867d66?w=400' },
    ],
    sizes: ['11x14"', '16x20"'],
    mockupImage: 'https://images.unsplash.com/photo-1513519245088-0e12902e35a6?w=400',
    popular: true,
    rating: 4.9,
    reviewCount: 312,
  },
];

// Get products by category
export const getProductsByCategory = (categoryId) => {
  if (categoryId === 'all') return PRODUCTS;
  return PRODUCTS.filter(p => p.category === categoryId);
};

// Get popular products
export const getPopularProducts = () => {
  return PRODUCTS.filter(p => p.popular);
};

// Get product by ID
export const getProductById = (productId) => {
  return PRODUCTS.find(p => p.id === productId);
};

// Get product image for specific variant
export const getProductImageForVariant = (product, variantId) => {
  const variant = product.variants.find(v => v.id === variantId);
  return variant?.image || product.mockupImage;
};
